import { chunk } from "lodash";

import {
  Category,
  CategoryCustomFieldAssignmentDTO,
  CategoryCustomFieldOrderDTO,
  CategoryDTO,
  CategoryStatusSet,
  CategoryStatusSetAssignmentDTO,
  CategoryType,
  CategoryUpdate,
  ComputedTreeFields,
  ListCategoriesFilters,
  ListCategoriesQueries,
  NewCategory,
  StatusSetAssignment,
} from "../models/categories";
import { CustomField, CustomFieldDTO } from "../models/customFields";
import { StatusSetId } from "../models/statusSets";
import { AssetsPaginatedResponse, AssetsResponse, ProjectId } from "../types";
import {
  CATEGORY_STATUS_STEP_SET_BATCH_FETCH_MAX,
  Endpoints,
} from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import { buildSearchString } from "./helpers";

// Raw literal query fragment (not run through buildSearchString) — ported
// verbatim from the app's getCategoriesAPIClient.ts so create/update/activate
// keep byte-identical wire output (and existing app tests keep passing).
const includedComputedTreeFieldsQuery =
  "includedComputedTreeFields=isLeaf,isRoot,subcategoryIds,path";

export const getCategoryClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => ({
  list: async ({
    maxDepth,
    parentId,
    internalId,
    updatedAt,
    categoryType = "asset",
    isDeleted,
    limit,
    includedComputedTreeFields = ["subcategoryIds", "isLeaf", "isRoot", "path"],
    includeDeleted,
  }: ListCategoriesFilters & ListCategoriesQueries = {}): Promise<
    CategoryDTO[]
  > => {
    const categories: CategoryDTO[] = [];
    let categoriesResponse: AssetsPaginatedResponse<CategoryDTO[]> | undefined;

    while (!categoriesResponse || categoriesResponse.pagination.cursorState) {
      const searchString = buildSearchString({
        "filter[maxDepth]": maxDepth,
        "filter[parentId]": parentId,
        "filter[internalId]": internalId,
        "filter[updatedAt]": updatedAt,
        categoryType,
        includeUid: true,
        isDeleted,
        limit,
        cursorState: categoriesResponse?.pagination.cursorState,
        includedComputedTreeFields: includedComputedTreeFields.toString(),
        includeDeleted,
      });
      categoriesResponse = await http.get<
        AssetsPaginatedResponse<CategoryDTO[]>
      >(
        `${http.getAssetsServiceBasePath(projectId, "v3")}/${
          Endpoints.Categories
        }${searchString ? `?${searchString}` : ""}`
      );
      categories.push(...categoriesResponse.results);
    }

    return categories;
  },

  // Single-page counterpart to `list` above.
  //
  // Reconciliation note (T001 categories batch, GH #2502): `list` auto-paginates
  // and is relied on by shared-web consumers (e.g. AssetsFilterPanel) that expect
  // a flat, fully-collected `CategoryDTO[]`. The app's own categories client has
  // never auto-paginated — it issues exactly one GET and hands the raw
  // `AssetsPaginatedResponse` (with `pagination.cursorState`) back to its caller,
  // using a different filter/query shape (`isActive`, `categoryType`,
  // `filter[maxDepth|parentId|internalId]`) than `list`'s. Rather than overload
  // `list` with a "single page" flag (which would risk changing its return shape
  // for existing callers) or duplicate the request-building logic inline in the
  // app, `listPage` is a dedicated lower-level primitive: it mirrors the app's
  // historical filters/query-building exactly (same param names, same param
  // order into `buildSearchString`) and returns one `AssetsPaginatedResponse`
  // page, unaltered. `list` keeps its existing auto-paginating contract and is
  // untouched by this addition.
  listPage: ({
    isActive,
    maxDepth,
    parentId,
    internalId,
    categoryType,
  }: ListCategoriesFilters = {}): Promise<
    AssetsPaginatedResponse<CategoryDTO[]>
  > => {
    const searchString = buildSearchString({
      includedComputedTreeFields: "isLeaf,isRoot,subcategoryIds,path",
      includeDeleted: !isActive,
      categoryType,
      "filter[maxDepth]": maxDepth,
      "filter[parentId]": parentId,
      "filter[internalId]": internalId,
    });

    return http.get<AssetsPaginatedResponse<CategoryDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId, "v3")}/${
        Endpoints.Categories
      }${searchString ? `?${searchString}` : ""}`
    );
  },

  batchGetStatusSetAssignments: async (
    categoryInternalIds: CategoryDTO["internalId"][],
    includeInherited = false
  ): Promise<StatusSetAssignment[]> => {
    const generateRequest = (ids: CategoryDTO["internalId"][]) =>
      http.post<{ results: CategoryStatusSet[] }>(
        `${http.getAssetsServiceBasePath(projectId)}/${
          Endpoints.CategoryStatusStepSets
        }/status-step-sets:batch-get${
          includeInherited ? "?includeInherited=true" : ""
        }`,
        { body: JSON.stringify({ ids }) }
      );

    if (categoryInternalIds.length > CATEGORY_STATUS_STEP_SET_BATCH_FETCH_MAX) {
      const results: CategoryStatusSet[] = [];
      for (const ids of chunk(
        categoryInternalIds,
        CATEGORY_STATUS_STEP_SET_BATCH_FETCH_MAX
      )) {
        results.push(...(await generateRequest(ids)).results);
      }
      return results;
    }

    return (await generateRequest(categoryInternalIds)).results;
  },

  batchGetStatusSetAssignmentsV3: async (
    categoryIds: CategoryDTO["id"][],
    categoryType: CategoryType,
    includeInherited = false
  ): Promise<StatusSetAssignment[]> => {
    const generateRequest = (ids: CategoryDTO["id"][]) =>
      http.post<{ results: StatusSetAssignment[] }>(
        `${http.getAssetsServiceBasePath(projectId, "v3")}/${
          Endpoints.CategoryTypes
        }/${categoryType}/${Endpoints.CategoryStatusSets}:batch-get`,
        { body: JSON.stringify({ categoryIds: ids, options: { includeInherited } }) }
      );

    if (categoryIds.length > CATEGORY_STATUS_STEP_SET_BATCH_FETCH_MAX) {
      const results: StatusSetAssignment[] = [];
      for (const ids of chunk(
        categoryIds,
        CATEGORY_STATUS_STEP_SET_BATCH_FETCH_MAX
      )) {
        results.push(...(await generateRequest(ids)).results);
      }
      return results;
    }

    return (await generateRequest(categoryIds)).results;
  },

  listCustomAttributesByCategory: async (
    categoryInternalId: CategoryDTO["internalId"]
  ): Promise<CustomFieldDTO[]> =>
    (
      await http.get<{ results: CustomFieldDTO[] }>(
        `${http.getAssetsServiceBasePath(projectId)}/${
          Endpoints.Categories
        }/${categoryInternalId}/custom-attributes`
      )
    ).results,

  batchGetById: async (
    ids: CategoryDTO["id"][],
    categoryType?: CategoryType,
    options?: {
      includeDeleted?: boolean;
      includedComputedTreeFields?: (keyof ComputedTreeFields)[];
    }
  ): Promise<CategoryDTO[]> => {
    const {
      includeDeleted = true,
      includedComputedTreeFields = [
        "subcategoryIds",
        "isLeaf",
        "isRoot",
        "path",
      ],
    } = options ?? {};

    const searchString = buildSearchString({
      categoryType,
      includeDeleted,
      includedComputedTreeFields: includedComputedTreeFields.toString(),
    });

    return (
      await http.post<{ results: CategoryDTO[] }>(
        `${http.getAssetsServiceBasePath(projectId, "v3")}/${
          Endpoints.Categories
        }:batch-get${searchString ? `?${searchString}` : ""}`,
        { body: JSON.stringify({ ids }) }
      )
    ).results;
  },

  create: async (
    category: NewCategory,
    categoryType: CategoryType = "asset"
  ): Promise<CategoryDTO> =>
    (
      await http.post<{ results: CategoryDTO[] }>(
        `${http.getAssetsServiceBasePath(projectId, "v3")}/${
          Endpoints.Categories
        }:batch-create?${includedComputedTreeFieldsQuery}&categoryType=${categoryType}`,
        { body: JSON.stringify([category]) }
      )
    ).results[0],

  update: async (
    categoryType: CategoryType,
    { id, ...updates }: Pick<Category, "id"> & CategoryUpdate
  ): Promise<CategoryDTO> =>
    (
      await http.patch<Record<Category["id"], CategoryDTO>>(
        `${http.getAssetsServiceBasePath(projectId, "v3")}/${
          Endpoints.Categories
        }:batch-patch?${includedComputedTreeFieldsQuery}&categoryType=${categoryType}`,
        { body: JSON.stringify({ [id]: updates }) }
      )
    )[id],

  delete: (
    id: Category["id"],
    categoryType: CategoryType = "asset"
  ): Promise<void> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v3")}/${
        Endpoints.Categories
      }:batch-destroy?categoryType=${categoryType}`,
      { body: JSON.stringify({ ids: [id] }) }
    ),

  activate: async (
    id: Category["id"],
    categoryType: CategoryType
  ): Promise<CategoryDTO> =>
    (
      await http.post<{ results: CategoryDTO[] }>(
        `${http.getAssetsServiceBasePath(projectId, "v3")}/${
          Endpoints.Categories
        }:batch-undelete?${includedComputedTreeFieldsQuery}&categoryType=${categoryType}`,
        { body: JSON.stringify({ ids: [id] }) }
      )
    ).results[0],

  deactivate: (
    id: Category["id"],
    categoryType: CategoryType = "asset"
  ): Promise<void> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v3")}/${
        Endpoints.Categories
      }:batch-delete?categoryType=${categoryType}`,
      { body: JSON.stringify({ ids: [id] }) }
    ),

  batchGetCustomFieldOrders: async (
    categoryInternalIds: Category["internalId"][]
  ): Promise<CategoryCustomFieldOrderDTO[]> =>
    (
      await http.post<{ results: CategoryCustomFieldOrderDTO[] }>(
        `${http.getAssetsServiceBasePath(projectId)}/${
          Endpoints.CategoryCustomAttributes
        }/order:batch-get`,
        { body: JSON.stringify({ ids: categoryInternalIds }) }
      )
    ).results,

  assignCustomField: (
    categoryInternalId: Category["internalId"],
    customFieldId: CustomField["id"]
  ): Promise<AssetsPaginatedResponse<CustomFieldDTO[]>> =>
    http.put<AssetsPaginatedResponse<CustomFieldDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.Categories
      }/${categoryInternalId}/custom-attributes/${customFieldId}`
    ),

  batchAssignCustomFields: (
    categoryInternalId: Category["internalId"],
    customFieldIds: CustomField["id"][]
  ): Promise<AssetsResponse<CategoryCustomFieldAssignmentDTO[]>> =>
    http.post<AssetsResponse<CategoryCustomFieldAssignmentDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.CategoryCustomAttributes
      }:batch-assign`,
      {
        body: JSON.stringify(
          customFieldIds.map((customAttributeId) => ({
            categoryId: categoryInternalId,
            customAttributeId,
          }))
        ),
      }
    ),

  unassignCustomField: (
    categoryInternalId: Category["internalId"],
    customFieldId: CustomField["id"]
  ): Promise<AssetsPaginatedResponse<CustomFieldDTO[]>> =>
    http.del<AssetsPaginatedResponse<CustomFieldDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.Categories
      }/${categoryInternalId}/custom-attributes/${customFieldId}`
    ),

  setCustomFieldOrder: (
    categoryInternalId: Category["internalId"],
    customAttributeIds: CustomField["id"][]
  ): Promise<CategoryCustomFieldOrderDTO> =>
    http.put<CategoryCustomFieldOrderDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.Categories
      }/${categoryInternalId}/custom-attributes/order`,
      {
        body: JSON.stringify({
          categoryId: categoryInternalId,
          customAttributeIds,
        }),
      }
    ),

  assignStatusSet: (
    categoryInternalId: Category["internalId"],
    statusSetId: StatusSetId
  ): Promise<CategoryStatusSetAssignmentDTO> =>
    http.put<CategoryStatusSetAssignmentDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.Categories
      }/${categoryInternalId}/status-step-set/${statusSetId}`
    ),

  batchAssignStatusSet: (
    assignments: Array<{
      categoryId: Category["id"];
      statusStepSetId: StatusSetId;
    }>,
    categoryType: CategoryType = "system"
  ): Promise<void> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v3")}/${
        Endpoints.CategoryTypes
      }/${categoryType}/${Endpoints.CategoryStatusSets}:batch-assign`,
      { body: JSON.stringify({ data: assignments }) }
    ),

  batchUnassignStatusSet: (
    categoryIds: Category["id"][],
    categoryType: CategoryType = "system"
  ): Promise<void> =>
    http.post(
      `${http.getAssetsServiceBasePath(projectId, "v3")}/${
        Endpoints.CategoryTypes
      }/${categoryType}/${Endpoints.CategoryStatusSets}:batch-unassign`,
      { body: JSON.stringify({ categoryIds }) }
    ),

  unassignStatusSet: (
    categoryInternalId: Category["internalId"]
  ): Promise<CategoryStatusSetAssignmentDTO> =>
    http.del<CategoryStatusSetAssignmentDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.Categories
      }/${categoryInternalId}/status-step-set`
    ),
});
